import { Button } from "@/components/ui/button";
import { ArrowRight, Twitter, Facebook, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/optibiz-logo-landscape1.svg";
import { useState } from "react";
import { showSuccessToast } from "./SuccessToast";
import { formService } from "@/services/formService";
import { useFormSubmission } from "@/hooks/use-form-submission";

const Footer = () => {
  const [email, setEmail] = useState("");

  const { isLoading, handleSubmit: submitForm } = useFormSubmission({
    submitFn: (data: string) => formService.submitDemoRequest({ email: data }),
    successMessage: "Subscription Confirmed! Thank you.",
    onSuccess: () => {
      showSuccessToast(email.split('@')[0], 'subscription');
      setEmail("");
    }
  });

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return; // The hook handles generic errors, but we can add specific validation toast if needed
    }

    await submitForm(email);
  };
  return (
    <footer className="bg-tasktide-bg dark:bg-gray-900 pt-20 pb-10 px-4 md:px-8 border-t border-gray-100 dark:border-gray-800">
      <div className="container mx-auto">
        {/* CTA Section */}
        <div className="bg-white dark:bg-gray-800 rounded-[1rem] p-12 md:p-20 text-center mb-20 relative overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-extrabold text-tasktide-dark dark:text-white mb-8 leading-tight">
              Transform Your Business with OptiBiz Suite
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-10">
              Join 500+ businesses already streamlining operations and driving growth. Schedule a demo to see how OptiBiz applications can transform your business.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto bg-gray-50 dark:bg-gray-900 p-2 rounded-lg md:rounded-full border border-optibiz-blue/20 dark:border-optibiz-blue/30">
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row w-full gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-6 py-3 outline-none text-gray-700 dark:text-gray-200 bg-transparent w-full placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-gradient-to-r from-optibiz-blue via-optibiz-green to-optibiz-orange dark:from-optibiz-orange dark:via-optibiz-yellow dark:to-optibiz-green text-white hover:from-optibiz-blue/90 hover:via-optibiz-green/90 hover:to-optibiz-orange/90 dark:hover:from-optibiz-orange/90 dark:hover:via-optibiz-yellow/90 dark:hover:to-optibiz-green/90 rounded-lg md:rounded-full px-8 py-6 w-full sm:w-auto font-bold relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span className="relative z-10 flex items-center">
                    {isLoading ? "Subscribing..." : <>Request a Demo <ArrowRight className="ml-2 w-4 h-4" /></>}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-optibiz-yellow/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-8">
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={logo} alt="OptiBiz Suite" className="h-14 md:h-16 p-0 transition-all duration-300 hover:scale-105 dark:[filter:brightness(0)_invert(1)]" />
            </Link>
            <p className="text-gray-500 dark:text-gray-400 text-md mb-6 max-w-xs">
              Comprehensive business management software to streamline operations, boost productivity, and drive growth for African enterprises.
            </p>
            <div className="flex gap-4">
              <a href="https://x.com/ubcreatives" className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-optibiz-blue hover:to-optibiz-green hover:text-white transition-all shadow-sm">
                <Twitter size={18} />
              </a>
              <a href="https://www.facebook.com/p/Unique-Brand-Creatives-61571124410211/" className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-optibiz-blue hover:to-optibiz-green hover:text-white transition-all shadow-sm">
                <Facebook size={18} />
              </a>
              <a href="https://www.instagram.com/p/DPvjOraCEnc/" className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-optibiz-blue hover:to-optibiz-green hover:text-white transition-all shadow-sm">
                <Instagram size={18} />
              </a>
              <a href="https://ke.linkedin.com/in/unique-brand-creatives" className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-optibiz-blue hover:to-optibiz-green hover:text-white transition-all shadow-sm">
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-tasktide-dark dark:text-white mb-6">Useful Links</h4>
            <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
              <li><a href="https://ubc.agency/" target="_blank" rel="noopener noreferrer" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">UBC Agency</a></li>
              <li><a href="https://uniquebrandcreatives.com/services/" target="_blank" rel="noopener noreferrer" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">Unique Brand Creatives</a></li>
              <li><a href="https://ubc.com/solution/technology/" target="_blank" rel="noopener noreferrer" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">UBC AI Solution</a></li>
              <li><Link to="/contact" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">Get Demo</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-tasktide-dark dark:text-white mb-6">Suite Solutions</h4>
            <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
              <li><Link to="/applications?app=crm" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">OptiBiz CRM</Link></li>
              <li><Link to="/applications?app=renttoday" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">RentToday</Link></li>
              <li><Link to="/applications?app=pos" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">OptiBiz POS</Link></li>
              <li><Link to="/applications?app=edu" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">OptiBiz EDU</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-tasktide-dark dark:text-white mb-6">Contact Us</h4>
            <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
              <li>info@optibiz.co.ke</li>
              <li>+254 769 653 431</li>
              <li>Daima Towers, Eldoret,<br />Uasin Gishu, Kenya</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-center gap-4 text-gray-500 dark:text-gray-400 text-sm font-medium">
          <p>&copy; 2025 OptiBiz Suite. All rights reserved. Powered by <a href="https://ubc.agency" target="_blank" rel="noopener noreferrer" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">U.B.C</a></p>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-tasktide-dark dark:hover:text-tasktide-orange transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
